/** Auth store — syncs Firebase auth state with Zustand. */

import { create } from "zustand";
import { auth, onAuthStateChanged, type FirebaseUser } from "@/firebase";
import { api } from "@/api/client";
import type { User } from "@/types";

interface AuthState {
  firebaseUser: FirebaseUser | null;
  user: User | null;
  loading: boolean;
  syncing: boolean;
  syncFailed: boolean;
  init: () => void;
  syncUser: () => Promise<void>;
  setUser: (user: User) => void;
}

let initialized = false;

export const useAuthStore = create<AuthState>((set, get) => ({
  firebaseUser: null,
  user: null,
  loading: true,
  syncing: false,
  syncFailed: false,

  init: () => {
    if (initialized) return;
    initialized = true;

    onAuthStateChanged(auth, (fbUser) => {
      if (fbUser) {
        set({ firebaseUser: fbUser, loading: false, syncFailed: false });
        // Eagerly sync backend user so RequireAuth sees syncing=true
        // before it can redirect to login
        const { user, syncing } = get();
        if (!user && !syncing) {
          get().syncUser();
        }
      } else {
        set({ firebaseUser: null, user: null, loading: false, syncFailed: false });
      }
    });
  },

  setUser: (user: User) => set({ user }),

  syncUser: async () => {
    const { firebaseUser, user, syncing } = get();
    // Already synced, already syncing, or no Firebase session — skip
    if (user || syncing || !firebaseUser) return;

    set({ syncing: true, syncFailed: false });
    try {
      const backendUser = await api.get<User>("/api/auth/me");
      set({ user: backendUser, syncing: false, syncFailed: false });
    } catch {
      // 401 = token invalid or user not in backend — clear state
      set({ user: null, syncing: false, syncFailed: true });
    }
  },
}));
